import { ReportPageBaseProps } from "@/components/report/types";
import { scrollTo } from "@/utils/objects";
import { usePageUserType } from "@/utils/router";
import { NavigateNext } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Link,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ClinicalDepartmentDialogLink from "./ClinicalDepartmentDialogLink";
import PrefectureSelect, {
  NO_SELECT,
  SelectedPrefecture,
} from "./PrefecturesSelect";
import { SLEEP_HOSPITAL_LIST, SleepHospital } from "./SleepHospital";

function CardTypography({
  title,
  content,
}: {
  title: string;
  content: string;
}) {
  return (
    <Stack direction="row" gap={1}>
      <Typography
        sx={{
          color: "text.secondary",
          fontFamily: "Noto Sans JP",
          fontWeight: 500,
          fontStyle: "Medium",
          fontSize: "14px",
          leadingTrim: "NONE",
          lineHeight: "185%",
          letterSpacing: "0.1px",
          textAlign: "justify",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.primary",
          fontFamily: "Noto Sans JP",
          fontWeight: 400,
          fontStyle: "Regular",
          fontSize: "14px",
          leadingTrim: "NONE",
          lineHeight: "185%",
          letterSpacing: "0.17px",
          textAlign: "justify",
        }}
      >
        {content}
      </Typography>
    </Stack>
  );
}

/** 1ページ当たりの表示件数 */
const PAGE_LIMIT = 10;
const HEADER_HEIGHT = 56;

interface SearchHospitalPageProps extends ReportPageBaseProps {}
export default function SearchHospitalPage({
  setIndicator,
}: SearchHospitalPageProps) {
  const { uuid } = useParams();
  const userType = usePageUserType();
  const searchCountElement = useRef<HTMLDivElement>(null);

  const [selectedPrefecture, setSelectedPrefecture] =
    useState<SelectedPrefecture>({
      prefecture: NO_SELECT,
      searchable: true,
    });

  const [isExcludeNeedNonReferralFee, setExcludeNeedNonReferralFee] =
    useState(false);

  const [sleepHospitals, setSleepHospitals] = useState<SleepHospital[]>([
    ...SLEEP_HOSPITAL_LIST,
  ]);

  const [page, setPage] = useState(1);

  useEffect(() => {
    setIndicator(false);
  }, [setIndicator]);

  /**
   * 条件に合うデータをフィルタし、引数のページ番号を設定し、スクロールを指定位置に移動します。
   */
  const search = useCallback(
    (page: number) => {
      setSleepHospitals(() =>
        SLEEP_HOSPITAL_LIST.filter(
          (value) =>
            selectedPrefecture.prefecture === NO_SELECT ||
            selectedPrefecture.prefecture === value.prefecture
        ).filter(
          (value) => !(isExcludeNeedNonReferralFee && value.needNonReferralFee)
        )
      );
      setPage(page);
    },
    [selectedPrefecture, isExcludeNeedNonReferralFee]
  );

  useEffect(() => search(1), [search]);

  /** 表示エリアが選択された際のハンドラ */
  const handleSelectPrefecture = useCallback(
    (selectedPrefecture: SelectedPrefecture) =>
      setSelectedPrefecture(selectedPrefecture),
    []
  );

  /** 選定療養費対象を表示しない */
  const handleExcludeNonReferralFee = useCallback(
    () => setExcludeNeedNonReferralFee((checked) => !checked),
    []
  );

  /** ページ番号が押下された時のハンドラ */
  const handleChangePage = useCallback(
    (_: React.ChangeEvent<unknown>, page: number) => {
      search(page);
      scrollTo(searchCountElement, { offsetTop: HEADER_HEIGHT });
    },
    [search]
  );
  return (
    <Stack sx={{ px: 6 }}>
      <Stack sx={{ pt: 12, pb: 8, gap: 2 }}>
        <Breadcrumbs maxItems={2} separator={<NavigateNext fontSize="small" />}>
          <Link
            href={`/${userType}/report/${uuid}`}
            color="text.secondary"
            style={{ textDecoration: "none" }}
          >
            <Typography variant="body2">レポートTOP</Typography>
          </Link>
          <Typography variant="body2">医療機関検索</Typography>
        </Breadcrumbs>
        <Typography
          variant="h5"
          sx={{ textAlign: "justify", color: "primary.main" }}
        >
          医療機関検索
        </Typography>
      </Stack>
      <Stack gap={4} sx={{ pb: 12 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Noto Sans JP",
            fontWeight: 400,
            fontStyle: "Regular",
            fontSize: "14px",
            leadingTrim: "NONE",
            lineHeight: "185%",
            letterSpacing: "0.17px",
            textAlign: "justify",
          }}
        >
          睡眠時無呼吸症候群や不眠症などの相談が可能な、日本睡眠学会認定の専門医療機関のリストです。
          <br />
          初診時に選定療養費が発生する医療機関も含まれておりますので、受診をご検討の際は、事前に各医療機関へお問い合わせいただくことをおすすめします。
          <br />
          なお、受診の際には「
          <Link
            href="disease_risk"
            target="_blank"
            variant="body2"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 400,
              fontStyle: "Regular",
              fontSize: "14px",
              leadingTrim: "NONE",
              lineHeight: "185%",
              letterSpacing: "0.17px",
              textAlign: "justify",
            }}
          >
            医療機関向けレポート
          </Link>
          」をご持参ください。
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Noto Sans JP",
            fontWeight: 400,
            fontStyle: "Regular",
            fontSize: "14px",
            leadingTrim: "NONE",
            lineHeight: "185%",
            letterSpacing: "0.17px",
            textAlign: "justify",
          }}
        >
          お近くに専門医療機関が存在しない場合、リストに表示されていない医療機関でも、一般的には
          <ClinicalDepartmentDialogLink>こちら</ClinicalDepartmentDialogLink>
          に記載の診療科でご相談いただけます。
        </Typography>
        <Stack gap={2}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 400,
              fontStyle: "Regular",
              fontSize: "12px",
              leadingTrim: "NONE",
              lineHeight: "175%",
              letterSpacing: "0.4px",
              textAlign: "justify",
            }}
          >
            出典：日本睡眠学会専門医療機関｜一般社団法人日本睡眠学会
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 400,
              fontStyle: "Regular",
              fontSize: "12px",
              leadingTrim: "NONE",
              lineHeight: "175%",
              letterSpacing: "0.4px",
              textAlign: "justify",
            }}
          >
            ※掲載内容に関するお問い合わせや、本リストのご利用により生じたいかなる損害についても、当社では一切の責任を負いかねますので、あらかじめご了承ください。
          </Typography>
        </Stack>
      </Stack>
      <Divider />
      <Stack sx={{ pt: 10, pb: 4, gap: 4 }}>
        <PrefectureSelect
          value={selectedPrefecture}
          onChange={handleSelectPrefecture}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isExcludeNeedNonReferralFee}
              onClick={handleExcludeNonReferralFee}
            />
          }
          label={
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                fontFamily: "Noto Sans JP",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "16px",
                leadingTrim: "NONE",
                lineHeight: "175%",
                letterSpacing: "0.15px",
              }}
            >
              選定療養費対象を表示しない
            </Typography>
          }
        />
      </Stack>
      <Stack sx={{ gap: 4 }}>
        <Stack
          direction="row"
          ref={searchCountElement}
          gap={1}
          justifyContent="end"
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.secondary",
              fontFamily: "Noto Sans JP",
              fontWeight: 500,
              fontStyle: "Medium",
              fontSize: "14px",
              leadingTrim: "NONE",
              lineHeight: "190%",
              letterSpacing: "0.1px",
              textAlign: "justify",
            }}
          >
            検索結果:
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.primary",
              fontFamily: "Noto Sans JP",
              fontWeight: 500,
              fontStyle: "Medium",
              fontSize: "14px",
              leadingTrim: "NONE",
              lineHeight: "190%",
              letterSpacing: "0.1px",
              textAlign: "justify",
            }}
          >
            {sleepHospitals.length}件
          </Typography>
        </Stack>
        {sleepHospitals
          .slice((page - 1) * PAGE_LIMIT, page * PAGE_LIMIT)
          .map((sleepHospital, i) => (
            <Card
              key={i}
              sx={{
                px: 6,
                py: 4,
                boxShadow: 0,
                border: 1,
                borderColor: "#E0E0E0",
                borderRadius: "8px",
              }}
            >
              <CardContent
                component={Stack}
                sx={{ p: 0, gap: 2, "&:last-child": { pb: 0 } }}
              >
                <Link
                  variant="body1"
                  target="_blank"
                  href={sleepHospital.url}
                  sx={{
                    fontFamily: "Noto Sans JP",
                    fontWeight: 400,
                    fontStyle: "Regular",
                    fontSize: "16px",
                    leadingTrim: "NONE",
                    lineHeight: "175%",
                    letterSpacing: "0.15px",
                  }}
                >
                  {sleepHospital.name}
                </Link>
                <CardTypography
                  title="所在地:"
                  content={`${sleepHospital.postalCode} ${sleepHospital.address}`}
                />
                <CardTypography
                  title="電話番号:"
                  content={`${sleepHospital.tel}`}
                />
                {sleepHospital.needNonReferralFee && (
                  <Box>
                    <Chip
                      label="選定療養費対象"
                      sx={{ backgroundColor: "#E8EAF6", color: "#3F51B5" }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
      </Stack>
      <Stack alignItems="center" sx={{ pt: 6, pb: 30 }}>
        <Pagination
          sx={{
            "& ul": {
              flexWrap: "nowrap",
            },
          }}
          count={Math.ceil(sleepHospitals.length / PAGE_LIMIT)}
          page={page}
          onChange={handleChangePage}
          color="primary"
        />
      </Stack>
    </Stack>
  );
}
